import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { enumCompanyBusinessType } from '@app/data-layer/enums/companies';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@app/data-layer/enums/documents';
import { CompanyModel } from '@app/data-layer/models/companies';
import { DocumentModel } from '@app/data-layer/models/documents';
import { USA_COUNTRY_CODE } from '@constants/constants';
import { requiresStateOfIncorporation } from '@constants/business-types';
import { enumTypeView } from '@enum/enumTypeView';
import { identifierEINValidator, identifierSSNValidator } from '@helpers/custom-validator';
import { EnumHelper } from '@helpers/enum.helper';
import { IItemMask } from '@interfaces/item-mask';
import { CbpAssignedNumberRequestService } from '@services/cbp-assigned-number-request.service';
import { CompaniesService } from '@services/companies.service';
import { DocumentsService } from '@services/documents.service';
import { OrganizationsService } from '@services/organizations.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { Observable, ReplaySubject } from 'rxjs';
import { finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'company-cbp-assigned-number-request-application-page',
  templateUrl: './company-cbp-assigned-number-request-application-page.component.html',
  styleUrls: ['./company-cbp-assigned-number-request-application-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyCbpAssignedNumberRequestApplicationPageComponent implements OnInit, OnDestroy {
  public formGroup: UntypedFormGroup;
  public isUnitedStates: boolean;
  public company: CompanyModel;
  public enumTypeView: typeof enumTypeView = enumTypeView;
  public identifierQualifier: enumIdentifierQualifier;
  public businessType: enumCompanyBusinessType;
  public enumIdentifierQualifier: typeof enumIdentifierQualifier = enumIdentifierQualifier;
  public itemMask: IItemMask = {} as IItemMask;
  public helperIdentifier: string = null;
  public poaFileUrl: string = '';
  public isLoadingDocUploadFlags: Map<enumDocumentType, boolean> = new Map();
  public uploadDocuments: Array<{ docType: number; file: File }> = [];
  public companyDocuments: DocumentModel[] = [];
  public isOpenPoaModal: boolean = false;

  public selectedDocumentTypesArray: enumDocumentType[] = [];
  public enumDocumentType: typeof enumDocumentType = enumDocumentType;
  public showIorSection: boolean = false;
  // #13233 — fixed US country for the State of Incorporation states dropdown (US-only field)
  public usCountryFc: UntypedFormControl = new UntypedFormControl({ code: USA_COUNTRY_CODE });

  private poaFile: File;

  public addressErrors: { [name: string]: IErrorForm } = {
    postalCode: {
      maxlength: 'FORM_ERROR.MAX_LENGTH',
    },
  };

  public identifierErrors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
    backError: '',
    notMatchEIN: 'FORM_ERROR.NOT_MATCH_EIN',
    wrongFormatIEN: 'FORM_ERROR.WRONG_FORMAT_EIN',
    notMatchSSN: 'FORM_ERROR.NOT_MATCH_SSN',
  };

  public dobErrors: IErrorForm = {
    required: 'FORM_ERROR.REQUIRED',
    minlength: 'FORM_ERROR.MIN_LENGTH',
    maxlength: 'FORM_ERROR.MAX_LENGTH',
  };

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();
  private DEFAULT_MASK: string = '0{13}';

  private maskArray: { [key: string]: IItemMask } = {
    EIN: {
      mask: '00-0000000AA',
      placeholder: 'NN-NNNNNNNAA',
      inputmode: 'text',
      maskErrorTooltip: 'MASK_ERROR.EIN',
      maskMaxLength: 11,
    },
    SSN: {
      mask: '000-00-0000',
      placeholder: 'NNN-NN-NNNN',
      inputmode: 'numeric',
      maskErrorTooltip: 'MASK_ERROR.ONLY_DIGITS',
      maskMaxLength: 9,
    },
  };

  private businessTypeDocuments: { [key: number]: enumDocumentType[] };

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private cbpAssignedNumberRequestService: CbpAssignedNumberRequestService,
    private organizationsService: OrganizationsService,
  ) {}

  ngOnInit() {
    const routeQueryParams = this.route.snapshot.queryParams;
    this.identifierQualifier = !!routeQueryParams?.identifierQualifier ? routeQueryParams?.identifierQualifier : '';
    this.businessType = !!routeQueryParams?.businessType ? Number(routeQueryParams?.businessType) : null;
    this.businessTypeDocuments = this.cbpAssignedNumberRequestService.getBusinessTypeDocuments();
    this.selectedDocumentTypesArray = this.businessTypeDocuments[this.businessType];

    this.company = this.companiesService.getCompany();
    this.showIorSection = !!this.company && !this.company.isImporterOfRecord;

    if (this.company?.id) {
      this.companiesService
        .getBrokerPoaFileUrl(this.company.id)
        .pipe(take(1))
        .subscribe((url: string) => {
          this.poaFileUrl = url;
          this.cdr.markForCheck();
        });
    } else {
      const organizationId = this.organizationsService.getOrganization()?.id;
      if (organizationId) {
        this.organizationsService
          .getBrokerPoaFileUrl(organizationId)
          .pipe(take(1))
          .subscribe((url: string) => {
            this.poaFileUrl = url;
            this.cdr.markForCheck();
          });
      }
    }
    this.initFormGroup();

    if (!this.showIorSection) {
      (this.formGroup.get('importerOfRecordContactInformation') as UntypedFormGroup)?.disable({ emitEvent: false });
    }

    this.documentsService
      .findCompanyDocument(enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY)
      .pipe(
        switchMap((document) => {
          if (!!document) {
            return this.getCompanyDocuments(true);
          } else {
            return this.documentsService.copyCompanyPoaToCbpRequest().pipe(
              takeUntil(this.destroy$),
              switchMap((_) => this.getCompanyDocuments(true)),
            );
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    if (!!this.identifierQualifier) {
      this.setIdentifierMaskAndPlaceHolder(this.identifierQualifier);

      if (this.identifierQualifier === enumIdentifierQualifier.SSN) {
        this.formGroup.get('dateOfBirth').setValidators(Validators.required);
      } else {
        this.formGroup.get('dateOfBirth').setValidators([]);
      }
      this.formGroup.get('dateOfBirth').updateValueAndValidity();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public documentName(docType: enumDocumentType): string {
    switch (docType) {
      case enumDocumentType.COMPANY_ARTICLES_OF_INCORPORATION:
        return 'LABELS.IOR_ARTICLES_OF_INCORPORATION';
      case enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY:
        return 'COMPANY.POWER_OF_ATTORNEY_TITLE';
      case enumDocumentType.COMPANY_ESTABLISHMENT_CERTIFICATE:
        return 'LABELS.ESTABLISHMENT_OF_CERTIFICATE';
      case enumDocumentType.OFFICERS_AUTHORIZATION_CONFIRMATION:
        return 'LABELS.OFFICERS_AUTHORIZATION_CONFIRMATION';
      case enumDocumentType.ID_DOCUMENT_1:
        return 'LABELS.ID_DOCUMENT_1';
      case enumDocumentType.ID_DOCUMENT_2:
        return 'LABELS.ID_DOCUMENT_2';
      case enumDocumentType.ID_DOCUMENT:
        return 'LABELS.ID_DOCUMENT';
      default:
        return;
    }
  }

  public documentHint(docType: enumDocumentType): string {
    switch (docType) {
      case enumDocumentType.COMPANY_ARTICLES_OF_INCORPORATION:
        return 'LABELS.IOR_ARTICLES_OF_INCORPORATION_DESC';
      case enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY:
        if (this.businessType === enumCompanyBusinessType.CORPORATION) {
          return 'COMPANY.POWER_OF_ATTORNEY_DESC';
        } else if (this.businessType === enumCompanyBusinessType.SOLE_PROPRIETOR) {
          return 'COMPANY.POWER_OF_ATTORNEY_NON_CORPORATE_DESC';
        } else if (this.businessType === enumCompanyBusinessType.INDIVIDUAL) {
          return 'COMPANY.POWER_OF_ATTORNEY_INDIVIDUAL_DESC';
        }
      case enumDocumentType.COMPANY_ESTABLISHMENT_CERTIFICATE:
        return 'LABELS.ESTABLISHMENT_OF_CERTIFICATE_DESC';
      case enumDocumentType.OFFICERS_AUTHORIZATION_CONFIRMATION:
        return 'LABELS.OFFICERS_AUTHORIZATION_CONFIRMATION_DESC';
      case enumDocumentType.ID_DOCUMENT_1:
        return 'LABELS.FIRST_SIGNATORY_ID_DOCUMENT_DESC';
      case enumDocumentType.ID_DOCUMENT_2:
        return 'LABELS.SECOND_SIGNATORY_ID_DOCUMENT_DESC';
      case enumDocumentType.ID_DOCUMENT:
        if (this.businessType === enumCompanyBusinessType.SOLE_PROPRIETOR) {
          return 'LABELS.IOR_OWNER_ID_DOCUMENT_DESC';
        } else if (this.businessType === enumCompanyBusinessType.INDIVIDUAL) {
          return 'LABELS.INDIVIDUAL_ID_DOCUMENT_DESC';
        }
      default:
        return;
    }
  }
  public documentHintNote(docType: enumDocumentType): string {
    switch (docType) {
      case enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY:
        if (this.businessType === enumCompanyBusinessType.CORPORATION) {
          return 'COMPANY.SIGNED_BY_TWO_OFFICERS';
        } else {
          return '';
        }
      default:
        return;
    }
  }

  public get businessTypeTranslationKey(): string {
    const businessTypeName = enumCompanyBusinessType[this.businessType];
    return 'LABELS.BUSINESS_TYPE_NAME.' + businessTypeName;
  }

  // #13233 — State of Incorporation shown only for US Corporation/LLC/Partnership
  public get isStateOfIncorporationVisible(): boolean {
    return this.isUnitedStates && requiresStateOfIncorporation(this.businessType);
  }

  public document(docType: enumDocumentType): DocumentModel {
    return !!this.company?.id
      ? this.companyDocuments.find((document: DocumentModel) => document.type === docType)
      : this.mapInMemoryFile(docType);
  }

  public uploadDocument(file: File, docType: enumDocumentType): void {
    if (docType === enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY) {
      this.setIsOpenPoaModal(true);
      this.poaFile = file;
      return;
    }

    if (this.company?.id) {
      this.uploadDocumentToServer(file, docType);
      return;
    }

    this.uploadDocuments.push({ docType: docType, file });
  }

  public deleteDocument(document: DocumentModel, docType: enumDocumentType): void {
    if (this.company?.id) {
      this.deleteDocumentFromServer(document.id, docType);
      return;
    }

    this.uploadDocuments = this.uploadDocuments.filter((file: { docType: number; file: File }) => {
      return file.docType !== docType;
    });
  }

  public updateDocumentState(documentId: number, state: enumDocumentState): void {
    this.companyDocuments = this.companyDocuments.map((document: DocumentModel) =>
      document.id === documentId
        ? {
            ...document,
            state,
          }
        : document,
    );
  }

  public onUploadPoaFile(uploadToCompany: boolean) {
    this.uploadDocumentToServer(this.poaFile, enumDocumentType.CBP_ASSIGNED_NUMBER_POWER_OF_ATTORNEY, uploadToCompany);
    this.setIsOpenPoaModal(false);
  }

  public setIsOpenPoaModal(value: boolean) {
    this.isOpenPoaModal = value;
    this.cdr.markForCheck();
  }

  private initFormGroup() {
    this.formGroup = this.cbpAssignedNumberRequestService.getFormGroup();
    this.formGroup.get('identifierQualifier').patchValue(this?.identifierQualifier, { emitEvent: false });
    this.formGroup.get('businessType').patchValue(this.businessType, { emitEvent: false });

    this.formGroup
      .get('address')
      .get('country')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((country) => {
        this.isUnitedStates = country.code === USA_COUNTRY_CODE;
        // #13233 — keep the State of Incorporation requirement in sync with the selected country
        this.applyStateOfIncorporationValidator();
        this.cdr.markForCheck();
      });

    // #13233 — the country may already be populated (e.g. navigating from the start step)
    const currentCountry = this.formGroup.get(['address', 'country']).value;
    this.isUnitedStates = !!currentCountry && currentCountry.code === USA_COUNTRY_CODE;
    this.applyStateOfIncorporationValidator();
  }

  // #13233 — State of Incorporation is mandatory for US Corporation/LLC/Partnership; cleared otherwise
  private applyStateOfIncorporationValidator(): void {
    const control = this.formGroup.get('stateOfIncorporation');
    if (!control) {
      return;
    }

    if (this.isStateOfIncorporationVisible) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
      control.patchValue(null);
    }
    control.updateValueAndValidity();
  }

  private uploadDocumentToServer(file: File, docType: number, uploadToCompany: boolean = false): void {
    this.setDocumentIsLoading(true, docType);
    this.documentsService
      .uploadDocument(
        {
          fileName: file.name,
          relatedEntityId: this.company?.id,
          relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          rootEntityId: this.company?.id,
          rootEntityType: enumDocumentRootEntityType.COMPANY,
          type: Number(docType),
        },
        file,
        this.company?.id,
      )
      .pipe(
        tap((id) => {
          if (id && uploadToCompany) {
            this.documentsService.copyPoaToCompany(id, file.name, false).pipe(take(1)).subscribe();
          }
        }),
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => this.setDocumentIsLoading(false, docType)),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cdr.detectChanges());
  }

  private deleteDocumentFromServer(documentId: number, docType: number): void {
    this.setDocumentIsLoading(true, docType);
    this.documentsService
      .deleteDocument(documentId, false)
      .pipe(
        switchMap(() => this.getCompanyDocuments(false)),
        finalize(() => {
          this.setDocumentIsLoading(false, docType);
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cdr.detectChanges());
  }

  private getCompanyDocuments(isShowLoader: boolean): Observable<DocumentModel[]> {
    return this.documentsService.getCompanyDocuments(this.company?.id, isShowLoader).pipe(
      tap((documents: DocumentModel[]) => {
        this.syncCompanyDocuments(documents);
      }),
    );
  }

  private syncCompanyDocuments(documents: DocumentModel[]) {
    this.companyDocuments = documents;

    const documentTypes = documents.map((doc) => doc.type);
    const selectedDocumentTypesArray = this.selectedDocumentTypesArray;

    let isAnyDocumentMissing: boolean = false;
    selectedDocumentTypesArray.forEach((docType) => {
      if (!documentTypes.includes(docType)) {
        isAnyDocumentMissing = true;
      }
    });

    this.cbpAssignedNumberRequestService.setIsAnyBusinessTypeRelatedDocsMissing(isAnyDocumentMissing);

    this.cdr.markForCheck();
  }

  private mapInMemoryFile(docType: enumDocumentType): DocumentModel {
    const file: File = this.uploadDocuments?.find((tes: { docType: number; file: File }) => tes.docType === docType)?.file;
    return {
      id: null,
      url: null,
      relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
      relatedEntityId: this.company?.id,
      rootEntityType: enumDocumentRootEntityType.COMPANY,
      rootEntityId: this.company?.id,
      fileName: file?.name,
      type: docType,
      state: enumDocumentState.UPLOADED,
    };
  }

  private setDocumentIsLoading(value: boolean, docType: any): void {
    this.isLoadingDocUploadFlags.set(docType, value);
  }

  private setIdentifierMaskAndPlaceHolder(valueQual: enumIdentifierQualifier) {
    let validatorFn;

    switch (valueQual) {
      case enumIdentifierQualifier.EIN:
        validatorFn = identifierEINValidator;
        break;
      case enumIdentifierQualifier.SSN:
        validatorFn = identifierSSNValidator;
        break;
      default:
        validatorFn = null;
        break;
    }

    this.setIdentitiesQualDependence(valueQual, validatorFn);
  }

  private setIdentitiesQualDependence(valueQual: enumIdentifierQualifier, validatorFn?: () => ValidatorFn) {
    const qualName: string = EnumHelper.getEnumKeyByValue(enumIdentifierQualifier, valueQual);
    this.itemMask = this.getItemMask(qualName);
    this.helperIdentifier = !qualName ? null : `FORM_HELPER.IDENTIFIER_${qualName}`;

    if (!validatorFn) {
      this.formGroup.get('identifier').clearValidators();
      this.formGroup.get('identifier').setValidators(null);
    } else {
      this.formGroup.get('identifier').setValidators([Validators.required, validatorFn()]);
    }
    this.formGroup.get('identifier').updateValueAndValidity({ emitEvent: false });
  }

  private getItemMask(key: string): IItemMask {
    const { mask = this.DEFAULT_MASK, placeholder = null, inputmode = 'text', maskErrorTooltip, maskMaxLength } = this.maskArray[key] || {};

    return { mask, placeholder, inputmode, maskErrorTooltip, maskMaxLength };
  }
}
